import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { productImageUrl } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, ImageOff, Lock, Package } from 'lucide-react';

function availabilityBadge(state, t, small = false) {
  const map = {
    in_stock: { cls: 'bg-[#E7F6EE] text-[#1F7A4D] border-[#CFEBD9]', label: t('product.availability.in_stock') },
    limited: { cls: 'bg-[#FFF3E0] text-[#B7791F] border-[#F3E0BE]', label: t('product.availability.limited') },
    out_of_stock: { cls: 'bg-[#FBEBEA] text-[#B42318] border-[#F3D3D0]', label: t('product.availability.out_of_stock') },
    unknown: { cls: 'bg-[#F6F8FB] text-[#4A4D52] border-[#E4E9F0]', label: t('product.availability.unknown') },
  };
  const v = map[state] || map.unknown;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 ${small ? 'py-0.5 text-[11px]' : 'py-1 text-xs'} font-medium ${v.cls}`}>
      <Package className="h-3 w-3" />
      {v.label}
    </span>
  );
}

export function ProductCard({ product, isAuth, portal = false }) {
  const { t } = useTranslation();
  const primary = product.is_favorite;
  const detailHref = portal ? `/portal/product/${product.id}` : `/product/${product.id}`;
  const [imgState, setImgState] = useState('loading'); // 'loading' | 'loaded' | 'error'
  return (
    <Link
      to={detailHref}
      data-testid="product-card"
      className={`pn-card pn-card-hover group relative flex flex-col overflow-hidden ${primary ? 'ring-1 ring-[#E2611C]/40' : ''}`}
    >
      <div className="relative aspect-[4/5] w-full overflow-hidden bg-[#F6F8FB]">
        {imgState === 'loading' && (
          <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-[#EEF1F6] via-[#F6F8FB] to-[#EEF1F6]" aria-hidden="true" />
        )}
        {imgState === 'error' ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-[#B1B4B9]">
            <ImageOff className="h-8 w-8" />
            <span className="text-[11px] uppercase tracking-wider">Kein Bild</span>
          </div>
        ) : (
          <img
            src={productImageUrl(product.id)}
            alt={product.name}
            loading="lazy"
            decoding="async"
            onLoad={() => setImgState('loaded')}
            onError={() => setImgState('error')}
            className={`h-full w-full object-contain p-3 transition-opacity duration-500 group-hover:scale-[1.02] ${imgState === 'loaded' ? 'opacity-100' : 'opacity-0'}`}
          />
        )}
        <div className="absolute left-3 top-3 flex items-center gap-2">
          {primary ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#FBEADD] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#E2611C]">Hauptfokus</span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#14171C] border border-[#E4E9F0]">
              <Lock className="h-3 w-3" /> {t('product.customer_specific')}
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#4A4D52]">{product.category_path || product.category_name || 'PlastoNord'}</div>
        <div className="mt-1 font-display text-lg font-semibold text-[#14171C] line-clamp-2">{product.name}</div>
        {product.default_code && (
          <div className="mt-1 font-mono text-xs text-[#4A4D52]">{t('product.reference')} {product.default_code}</div>
        )}
        {(product.tech_specs?.verarbeitungsart || product.tech_specs?.base) && (
          <dl className="mt-2 space-y-0.5 text-xs" data-testid="product-card-specs">
            {product.tech_specs?.verarbeitungsart && (
              <div className="flex items-baseline gap-2">
                <dt className="min-w-[110px] shrink-0 font-medium text-[#4A4D52]">{t('product.verarbeitungsart')}</dt>
                <dd className="text-[#14171C]">{product.tech_specs.verarbeitungsart}</dd>
              </div>
            )}
            {product.tech_specs?.base && (
              <div className="flex items-baseline gap-2">
                <dt className="min-w-[110px] shrink-0 font-medium text-[#4A4D52]">{t('product.base')}</dt>
                <dd className="text-[#14171C]">{product.tech_specs.base}</dd>
              </div>
            )}
          </dl>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {availabilityBadge(product.availability, t)}
          {product.offer_price ? (
            <Badge variant="outline" className="rounded-full border-[#1F7A4D]/40 bg-[#E7F6EE] text-[#1F7A4D] font-mono">
              {product.offer_price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/{product.offer_unit || 'kg'}
            </Badge>
          ) : (
            <Badge variant="outline" className="rounded-full border-[#E2611C]/50 bg-white text-[#E2611C]">
              {t('product.price_on_request')}
            </Badge>
          )}
        </div>
        <div className="mt-auto flex items-center justify-between pt-5">
          <span data-testid="product-card-details-button" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#14171C] group-hover:text-[#E2611C]">
            {t('product.details')} <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
          </span>
          <span
            role="button"
            data-testid="product-card-request-sample-button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `/request/${product.id}?type=sample`; }}
            className="text-xs font-semibold text-[#4A4D52] hover:text-[#E2611C]"
          >
            {t('product.request_sample')} →
          </span>
        </div>
      </div>
    </Link>
  );
}
